import React, { useCallback, useEffect, useRef, useState } from 'react';
import { simulateWaMessage, simulateWaReset, fetchSimulateSession, fetchWaHealth } from '../../../utils/adminWhatsapp';

function BotBubble({ reply, onChip }) {
  const { type, content, buttons, list_rows: listRows } = reply;
  return (
    <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-gray-100 px-3 py-2 text-sm">
      <p className="whitespace-pre-wrap">{content}</p>
      {buttons?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {buttons.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => onChip({ button_id: b.id })}
              className="text-xs px-2 py-1 bg-white border rounded-lg hover:bg-[#659A9B]/10"
            >
              {b.title}
            </button>
          ))}
        </div>
      )}
      {listRows?.length > 0 && (
        <div className="mt-2 space-y-1">
          {listRows.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => onChip({ list_id: row.id })}
              className="block w-full text-left text-xs px-2 py-1 bg-white border rounded hover:bg-[#659A9B]/10"
            >
              <span className="font-medium">{row.title}</span>
              {row.description && <span className="text-gray-500"> — {row.description}</span>}
            </button>
          ))}
        </div>
      )}
      {type === 'document' && <p className="text-xs mt-1 text-gray-500">[Document]</p>}
    </div>
  );
}

export default function WaSimulatorChat({ phones = [], defaultPhone = '' }) {
  const [phone, setPhone] = useState(defaultPhone);
  const [text, setText] = useState('');
  const [thread, setThread] = useState([]);
  const [session, setSession] = useState(null);
  const [health, setHealth] = useState(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const refreshSession = useCallback(async () => {
    if (!phone) return;
    try {
      const r = await fetchSimulateSession(phone);
      setSession(r.session);
    } catch {
      setSession(null);
    }
  }, [phone]);

  useEffect(() => {
    fetchWaHealth().then(setHealth).catch(() => setHealth(null));
  }, []);

  useEffect(() => {
    if (defaultPhone) setPhone(defaultPhone);
  }, [defaultPhone]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  const send = async (payload = {}) => {
    if (!phone) return;
    const userText = payload.text ?? text;
    if (userText?.trim() || payload.button_id || payload.list_id) {
      setThread((t) => [...t, { role: 'user', content: userText || `[${payload.button_id || payload.list_id}]` }]);
    }
    setText('');
    setSending(true);
    try {
      const r = await simulateWaMessage({
        phone,
        text: userText || '',
        button_id: payload.button_id,
        list_id: payload.list_id,
      });
      setSession(r.session);
      const replies = r.replies || [];
      setThread((t) => [
        ...t,
        ...replies.map((rep) => ({ role: 'bot', reply: rep })),
      ]);
    } catch (e) {
      setThread((t) => [...t, { role: 'bot', reply: { type: 'text', content: `Error: ${e.message}` } }]);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (!phone) return;
    await simulateWaReset(phone);
    setThread([]);
    setSession(null);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border rounded-xl flex flex-col h-[70vh]">
        <div className="p-3 border-b flex flex-wrap gap-2 items-center">
          <select
            className="border rounded-lg px-2 py-1 text-sm flex-1 min-w-[140px]"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          >
            <option value="">Select phone…</option>
            {phones.map((p) => (
              <option key={p.phone} value={p.phone}>{p.phone} {p.agent_name ? `(${p.agent_name})` : ''}</option>
            ))}
          </select>
          <input
            className="border rounded-lg px-2 py-1 text-sm flex-1 min-w-[120px]"
            placeholder="Or +91…"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <button type="button" onClick={reset} className="text-xs px-2 py-1 border rounded-lg">Reset</button>
        </div>
        {!health?.bot_enabled && (
          <p className="text-xs bg-amber-50 text-amber-800 px-3 py-2">WA_BOT_ENABLED is off — enable in .env</p>
        )}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {thread.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'user' ? (
                <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-[#659A9B] text-white px-3 py-2 text-sm">
                  {m.content}
                </div>
              ) : (
                <BotBubble reply={m.reply} onChip={send} />
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <form
          className="p-3 border-t flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <input
            className="flex-1 border rounded-lg px-3 py-2 text-sm"
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={sending || !phone}
          />
          <button
            type="submit"
            disabled={sending || !phone}
            className="px-4 py-2 bg-[#1B375B] text-white rounded-lg text-sm disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </div>
      <div className="bg-white border rounded-xl p-4 h-[70vh] overflow-y-auto">
        <h3 className="font-semibold text-[#1B375B] mb-2">Debug</h3>
        <button type="button" className="text-xs text-[#659A9B] mb-2" onClick={refreshSession}>Refresh session</button>
        {session ? (
          <pre className="text-[10px] bg-gray-50 p-2 rounded overflow-auto max-h-[60vh]">
            {JSON.stringify({ id: session.id, stage: session.stage, state: session.state, cart: session.booking_cart }, null, 2)}
          </pre>
        ) : (
          <p className="text-xs text-gray-500">No active session</p>
        )}
      </div>
    </div>
  );
}
